import React, { useCallback, useState } from "react";
import DashboardModal from "../components/DashboardModal";
import { updatePropertyFinancing } from "../services/ownerPatrimonyService";
import { IFinancingInsight } from "../types/ownerDashboard.types";
import { buildFinancingFormValuesFromInsight } from "../utils/buildFinancingFormValues";
import {
  FinancingFormValues,
  buildFinancingPayload,
  validateFinancingForm,
} from "../utils/validateFinancingForm";
import FinancingFormFields from "./FinancingFormFields";

interface EditFinancingModalProps {
  insight: IFinancingInsight;
  propertyLabel: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditFinancingModal({
  insight,
  propertyLabel,
  onClose,
  onSuccess,
}: EditFinancingModalProps) {
  const [values, setValues] = useState<FinancingFormValues>(() =>
    buildFinancingFormValuesFromInsight(insight)
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback(
    <K extends keyof FinancingFormValues>(field: K, value: FinancingFormValues[K]) => {
      setValues((previous) => ({ ...previous, [field]: value }));
    },
    []
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const validationError = validateFinancingForm(values);

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildFinancingPayload(values);

    if (!payload) {
      setError("Revise os campos do financiamento.");
      return;
    }

    setIsSubmitting(true);

    try {
      await updatePropertyFinancing(insight.id, payload);
      onSuccess();
      onClose();
    } catch {
      setError("Não foi possível atualizar o financiamento.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardModal title="Editar financiamento" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
            Imóvel
          </p>
          <p className="mt-1 text-sm font-semibold text-[#f0f6fc]">{propertyLabel}</p>
        </div>

        <FinancingFormFields values={values} onChange={handleChange} />

        {error && <p className="text-sm text-[#f85149]">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full bg-[#6ea8ff] px-4 py-2.5 text-sm font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Salvando..." : "Salvar alterações"}
        </button>
      </form>
    </DashboardModal>
  );
}
